<!--
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
-->
<script lang="ts">
  import {
    MarkupNode,
    normalizeOpenApiViewMode,
    openApiLoader,
    openApiPresentation,
    openApiViewModeAttr,
    type OpenApiBlockResult,
    type OpenApiPresentation,
    type OpenApiSpec
  } from '@hcengineering/text'
  import { onDestroy } from 'svelte'

  import { mountSwaggerUi, type SwaggerUiMount } from '../../openapi/swaggerUi'

  /**
   * An OpenAPI block, read-only.
   *
   * This is the same block outside the editor — a comment, an activity entry, a document
   * being read rather than written — and it is read-only in the strong sense: there is no
   * toggle, nothing is written back, and the mode is taken as the document has it. A block an
   * author left showing its source shows its source here too, which is the point of
   * persisting the mode at all.
   *
   * What it draws with is the upstream Swagger UI, mounted through the one adapter the editor
   * uses as well (file://./../../openapi/swaggerUi.ts), and what it decides is decided by the
   * same `openApiPresentation` the editor's node view calls. Neither the look nor the rules
   * can drift between the two.
   */

  export let node: MarkupNode
  export let preview = false

  // The text is the only thing the document holds, and it is shown as-is whenever the
  // specification cannot be read, so nothing the author wrote is hidden behind a failure.
  $: source = (node.content ?? []).map((child) => child.text ?? '').join('\n')
  $: mode = normalizeOpenApiViewMode(node.attrs?.[openApiViewModeAttr])

  // A URL is fetched, once per address, by the loader shared with the editor. Inline text
  // resolves in the same tick, so it never flashes an empty block.
  let result: OpenApiBlockResult | undefined
  $: result = openApiLoader.peek(source)
  $: if (result === undefined && source.trim().length > 0) {
    const pending = source
    void openApiLoader.load(source).then((loaded) => {
      if (pending === source) result = loaded
    })
  }

  let shown: OpenApiPresentation
  $: shown = openApiPresentation(result, mode, result === undefined ? 'loading' : 'ready')

  // A compact preview is a thumbnail of the page, not the page. Standing up a Swagger UI for
  // every one of them — a list of comments can hold many — would cost far more than the few
  // lines of it that would be visible, so a thumbnail shows the text of the block instead.
  $: spec = preview ? undefined : shown.spec

  let host: HTMLDivElement | undefined
  let mount: SwaggerUiMount | undefined
  let mounted: OpenApiSpec | undefined
  let mountFailed = false

  $: syncMount(host, spec)

  function syncMount (element: HTMLDivElement | undefined, next: OpenApiSpec | undefined): void {
    if (mount !== undefined && mounted === next) return

    mount?.destroy()
    mount = undefined
    mounted = next
    mountFailed = false

    if (element === undefined || next === undefined) return

    const started = mountSwaggerUi(element, next)
    mount = started
    started.ready.catch(() => {
      // The only thing that can fail here is fetching the chunk holding Swagger UI. The block
      // still has its text, so it falls back to showing it rather than to showing nothing.
      if (mount === started) mountFailed = true
    })
  }

  onDestroy(() => {
    mount?.destroy()
  })
</script>

{#if source.trim().length > 0}
  {#if spec !== undefined && !mountFailed}
    <div class="openapiSwagger" bind:this={host} />
  {:else}
    <pre class="proseCodeBlock" class:preview><code>{source}</code></pre>
  {/if}
  {#if mountFailed}
    <div class="openapiNodeError">The API documentation viewer could not be loaded.</div>
  {:else if shown.message !== undefined}
    <div class="openapiNodeError" class:openapiNodeNote={!shown.messageIsError}>{shown.message}</div>
  {/if}
  {#if shown.note !== undefined && spec !== undefined}
    <div class="openapiNodeError openapiNodeNote">{shown.note}</div>
  {/if}
{/if}

<style lang="scss">
  .openapiNodeError {
    margin-top: 0.25rem;
    color: var(--theme-error-color);
    font-size: 0.8125rem;

    &.openapiNodeNote {
      color: var(--theme-dark-color);
    }
  }

  // A thumbnail is bounded so that one large specification cannot push everything else out.
  pre.preview {
    max-height: 12rem;
    overflow: hidden;
  }
</style>
